import { useCallback, useLayoutEffect, useRef, useState } from "react";

export const useScrollbarState = ({
  initialHasScrollableContentBelow = false,
  isEnabled,
}: {
  initialHasScrollableContentBelow?: boolean;
  isEnabled: boolean;
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [hasVisibleScrollbar, setHasVisibleScrollbar] = useState(false);
  const [hasScrollableContentBelow, setHasScrollableContentBelow] = useState(
    initialHasScrollableContentBelow
  );

  const updateScrollbarVisibility = useCallback(() => {
    const contentElement = contentRef.current;
    if (contentElement === null) {
      return;
    }

    const scrollContainer = contentElement.parentElement;
    if (scrollContainer === null) {
      return;
    }

    const hasOverflow = scrollContainer.scrollHeight > scrollContainer.clientHeight;
    setHasVisibleScrollbar(hasOverflow);
    setHasScrollableContentBelow(
      hasOverflow &&
        scrollContainer.scrollTop < scrollContainer.scrollHeight - scrollContainer.clientHeight - 1
    );
  }, []);

  useLayoutEffect(() => {
    if (!isEnabled) {
      setHasVisibleScrollbar(false);
      setHasScrollableContentBelow(initialHasScrollableContentBelow);
      return;
    }

    const contentElement = contentRef.current;
    if (contentElement === null) {
      return;
    }

    const scrollContainer = contentElement.parentElement;
    if (scrollContainer === null) {
      return;
    }

    updateScrollbarVisibility();

    const resizeObserver = new ResizeObserver(updateScrollbarVisibility);
    resizeObserver.observe(scrollContainer);
    resizeObserver.observe(contentElement);
    scrollContainer.addEventListener("scroll", updateScrollbarVisibility, { passive: true });
    window.addEventListener("resize", updateScrollbarVisibility);

    return () => {
      resizeObserver.disconnect();
      scrollContainer.removeEventListener("scroll", updateScrollbarVisibility);
      window.removeEventListener("resize", updateScrollbarVisibility);
    };
  }, [initialHasScrollableContentBelow, isEnabled, updateScrollbarVisibility]);

  return {
    contentRef,
    hasVisibleScrollbar,
    hasScrollableContentBelow,
  };
};
